import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldArray, FormikProps } from 'formik';
import { ConfirmModal, CustomButton } from '../../../../../components';
import { AddIcon } from '../../../../../assets/icons/AddIcon';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { matchData } from '../../../../../constant/play';
import uniqId from 'uniqid';
import { IMatch, PlayValues } from '../PlayTag/PlayTag';
import { openModal } from '../../../../../features/modal/modalSlice';
import { ModalAction } from '../../../../../types/modal';
import { Court, MatchResult, Team } from '../../../../../types/tournament';
import { Match } from '../Match/Match';
import {
  ClassFormatEnum,
  ClassTypeEnum,
  GroupStandingsTieBreakerEnum,
} from '../../../../../types/enums';
import { selectClass } from '../../../../../features/class/classSelectors';
import { selectTournament } from '../../../../../features/tournament/tournamentSelectors';
import { useGetCurrentOrganization } from '../../../../../features/organizations/organizationsSelectors';
import { useQuery } from '@tanstack/react-query';
import { getOrganizationTours } from '../../../../../apis/tours';
import { CloseCircle } from '../../../../../assets/icons/CloseCircle';
import { GroupStandings } from '../GroupStandings/GroupStandings';
import { StandingsExplainPanel } from '../StandingsExplain/StandingsExplainPanel';
import { MatchGroupSubsection } from '../MatchGroupSubsection/MatchGroupSubsection';
import {
  assignTeamsForElmMatchesForGroup,
  devAutoScoreGroupMatches,
} from '../../../../../features/tournament/actionCreators';
import { boxLeagueElimAssignFinalButtonVisible } from '../../../../../utils/boxLeaguePlayHelpers';
import { ClassValues } from '../../../TournamentClasses/components/ClassesTag/ClassesTag';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { InfoCircleFill } from 'react-bootstrap-icons';
import { groupMatchesByBracketCell } from '../../../../../utils/groupMatchesByBracketCell';
import {
  getBracketCellId,
  recordEliminationMatchesRemoval,
  recordGroupMatchesRemoval,
} from '../../../../../utils/bracketCellPendingDeletes.util';
import { buildMatchRenumberUpdates } from '../../../../../utils/matchIdRenumber.util';
import {
  recordBracketCellCreate,
  bracketCellRubberCount,
} from '../../../../../utils/bracketCellCreate.util';
import {
  implicitSectionProgressionMode,
  inferSectionProgressionMode,
  progressionModeForNewMatchup,
} from '../../../../../utils/matchupSectionProgression.util';
import {
  isTeamsGroupBracketCells as isTeamsGroupBracketCellsFormat,
  isEliminationPlayMatch,
  shouldRenderBracketCellSeriesInElimination,
} from '../../../../../utils/matchSectionRouting.util';
import { BracketCellSeries } from '../BracketCellSeries/BracketCellSeries';
import { MatchupSectionToolbar } from './MatchupSectionToolbar';
import {
  listIncludesTeamPoints,
  resolveGroupStandingsTieBreakerResolution,
} from '../../../../../utils/groupStandingsTieBreakers';

interface MatchSectionProps {
  formikArg: FormikProps<PlayValues>;
  courts?: Array<Court>;
  onPlayDataRefresh?: () => Promise<void>;
}

const devTools = process.env.REACT_APP_ENV === 'dev' || false;

export const MatchSection: FC<MatchSectionProps> = React.memo(
  ({ formikArg, courts = [], onPlayDataRefresh }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { classCollection, currentClassId } = useAppSelector(selectClass);
    const currentClass = classCollection.find(
      (data) => data.classId === currentClassId
    ) as ClassValues;
    const { data: tournamentData } = useAppSelector(selectTournament);
    const currentOrganization = useGetCurrentOrganization();
    const tourSetting = tournamentData?.settings?.tour;
    const tourIdValue =
      typeof tourSetting === 'object' ? (tourSetting as { id?: number })?.id : tourSetting;
    const { data: toursData } = useQuery({
      queryKey: ['organizationTours', currentOrganization?.id],
      queryFn: () => getOrganizationTours(currentOrganization!.id),
      enabled: !!currentOrganization?.id && !!tourIdValue,
    });
    const teamCategoryStandings =
      currentClass?.type === ClassTypeEnum.TEAMS ? currentClass.categoryStandings : undefined;

    const groups = formikArg.values.tournamentGroups;
    const matches = formikArg.values.tournamentMatches;
    const prefix = 'creativeDetail-';
    const buttonPrefix = 'button-';

    const isBoxLeagueMode =
      (currentClass?.format === ClassFormatEnum.BOX_LEAGUE ||
        currentClass?.format === ClassFormatEnum.BOX_LEAGUE_ELIM) &&
      currentClass?.type !== ClassTypeEnum.TEAMS;

    const isTeamsGroupBracketCells = isTeamsGroupBracketCellsFormat(
      currentClass?.type,
      currentClass?.format
    );
    const isTeamsClass = currentClass?.type === ClassTypeEnum.TEAMS;
    const rankingUsesTeamPoints = useMemo(() => {
      const selectedTour = toursData?.items?.find(
        (tour: { id: number }) => tour.id === Number(tourIdValue)
      ) as { groupStandingsTieBreakers?: GroupStandingsTieBreakerEnum[] | null } | undefined;
      const resolved = resolveGroupStandingsTieBreakerResolution({
        classTieBreakers: currentClass?.useInheritedGroupStandingsTieBreakers
          ? null
          : currentClass?.groupStandingsTieBreakers ?? null,
        tournamentTieBreakers: tournamentData?.settings?.groupStandingsTieBreakers ?? null,
        tourTieBreakers: selectedTour?.groupStandingsTieBreakers ?? null,
        organizationTieBreakers: currentOrganization?.groupStandingsTieBreakers ?? null,
        isTeamsClass,
        rulesVersion: currentClass?.groupStandingsRulesVersion ?? 1,
      });
      return listIncludesTeamPoints(
        currentClass?.groupStandingsTieBreakerResolution?.effective ?? resolved.effective
      );
    }, [
      currentClass?.groupStandingsTieBreakerResolution?.effective,
      currentClass?.groupStandingsTieBreakers,
      currentClass?.groupStandingsRulesVersion,
      currentClass?.useInheritedGroupStandingsTieBreakers,
      currentOrganization?.groupStandingsTieBreakers,
      isTeamsClass,
      tourIdValue,
      tournamentData?.settings?.groupStandingsTieBreakers,
      toursData?.items,
    ]);
    const showEliminationBracketCells = shouldRenderBracketCellSeriesInElimination(
      currentClass?.type
    );
    const tiebreakerLabel = t('creativeDetail-bracketCellAddDeciderMatch');
    const groupBracketCellRubberCount = useMemo(
      () =>
        bracketCellRubberCount(
          { variant: 'group' },
          currentClass,
          formikArg.values.tournamentCategories
        ),
      [currentClass, formikArg.values.tournamentCategories]
    );
    const eliminationBracketCellRubberCount = useMemo(
      () =>
        bracketCellRubberCount(
          { variant: 'elimination' },
          currentClass,
          formikArg.values.tournamentCategories
        ),
      [currentClass, formikArg.values.tournamentCategories]
    );

    const progressionModeForSection = (
      cells: ReturnType<typeof groupMatchesByBracketCell>,
      variant: 'group' | 'elimination'
    ) =>
      progressionModeForNewMatchup(
        inferSectionProgressionMode(
          cells.filter((cell) => !cell.isLegacySingle),
          formikArg.values.bracketCells,
          implicitSectionProgressionMode(variant)
        )
      );

    const handleAddBracketCell = useCallback(
      (context: Parameters<typeof recordBracketCellCreate>[1]) => {
        if (
          bracketCellRubberCount(context, currentClass, formikArg.values.tournamentCategories) === 0
        ) {
          return;
        }
        recordBracketCellCreate(
          formikArg,
          context,
          currentClass,
          formikArg.values.tournamentCategories,
          tiebreakerLabel
        );
      },
      [formikArg, currentClass, tiebreakerLabel]
    );

    const handleAddTeamForBracketCell = useCallback(
      (bracketCellId: string, fieldName: string) => {
        dispatch(
          openModal({
            currentModal: ModalAction.ADD_MATCH_TEAM,
            modalOpen: true,
            data: {
              matches,
              setMatches: formikArg.setFieldValue,
              bracketCellId,
              fieldName,
            },
          })
        );
      },
      [dispatch, matches, formikArg.setFieldValue]
    );

    const handleCategoryRename = useCallback(
      (categoryId: string, newName: string) => {
        const categories = formikArg.values.tournamentCategories;
        const index = categories.findIndex((c) => c.id.toString() === categoryId.toString());

        if (index !== -1) {
          formikArg.setFieldValue(`tournamentCategories.${index}.name`, newName);
        }
      },
      [formikArg]
    );

    const setSortKey = () => {
      const matchArray = Object.values(matches)
        .filter(({ id }) => Boolean(id))
        .sort((a, b) => a.id?.toString().localeCompare(b.id?.toString()));
      const groupsWithMatches =
        (groups.length &&
          groups.reduce((acc, group) => {
            const matchForGroupArray = matchArray.length
              ? matchArray.filter((match) => match?.tournamentGroup?.id === group.id)
              : [];
            return [...acc, ...matchForGroupArray];
          }, [] as any[])) ||
        [];

      const eleminationMatches = matchArray.length
        ? matchArray.filter((match: IMatch) =>
            currentClass?.format === ClassFormatEnum.GROUP ||
            currentClass?.format === ClassFormatEnum.GROUP_BRACKET ||
            currentClass?.format === ClassFormatEnum.GROUP_ONLY ||
            isBoxLeagueMode
              ? isEliminationPlayMatch(match)
              : match
          )
        : [];

      const sortIds = [...(groupsWithMatches as any[]), ...eleminationMatches].map(
        (item, index) => ({
          id: item.id,
          sortKey: index + 1,
        })
      );

      const updates = buildMatchRenumberUpdates(matches, sortIds, {
        skipMatchId: (match) =>
          currentClass?.type === ClassTypeEnum.TEAMS && Boolean(getBracketCellId(match)),
      });

      updates.forEach((update) => {
        if (update.sortKey !== undefined) {
          formikArg.setFieldValue(`tournamentMatches.${update.id}.sortKey`, update.sortKey);
        }
        if (update.matchId !== undefined) {
          formikArg.setFieldValue(`tournamentMatches.${update.id}.matchId`, update.matchId);
        }
        if (update.homeTeamPlaceholder !== undefined) {
          formikArg.setFieldValue(
            `tournamentMatches.${update.id}.homeTeamPlaceholder`,
            update.homeTeamPlaceholder
          );
        }
        if (update.awayTeamPlaceholder !== undefined) {
          formikArg.setFieldValue(
            `tournamentMatches.${update.id}.awayTeamPlaceholder`,
            update.awayTeamPlaceholder
          );
        }
      });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(setSortKey, [groups, matches, Object.keys(matches).length, currentClass]);

    const handleAddTeam = useCallback(
      (matchId: string | number, fieldName: string) => {
        dispatch(
          openModal({
            currentModal: ModalAction.ADD_MATCH_TEAM,
            modalOpen: true,
            data: { matches, setMatches: formikArg.setFieldValue, matchId, fieldName },
          })
        );
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [matches]
    );

    const handleAddPlayer = useCallback(
      (teamId: string | number, fieldName: string, matchId: string | number) => {
        dispatch(
          openModal({
            currentModal: ModalAction.ADD_MATCH_PLAYER,
            modalOpen: true,
            data: {
              matches,
              setMatches: formikArg.setFieldValue,
              teamId,
              fieldName,
              matchId,
            },
          })
        );
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [matches]
    );

    const handleOpenResultModal = useCallback(
      (
        results?: Array<MatchResult>,
        matchId?: number | string,
        homeTeam?: Team | null,
        awayTeam?: Team | null
      ) => {
        dispatch(
          openModal({
            currentModal: ModalAction.EDIT_MATCH_RESULT,
            modalOpen: true,
            data: { results, matchId, homeTeam, awayTeam },
          })
        );
      },
      [dispatch]
    );

    const eliminationMatches = Object.values(matches)
      .filter(({ id }) => Boolean(id))
      .sort((a, b) => Number(a.sortKey) - Number(b.sortKey))
      .filter((match) =>
        currentClass?.format === ClassFormatEnum.GROUP ||
        currentClass?.format === ClassFormatEnum.GROUP_BRACKET ||
        currentClass?.format === ClassFormatEnum.GROUP_ONLY ||
        isBoxLeagueMode
          ? isEliminationPlayMatch(match)
          : match
      );
    const eliminationBracketCellGroups = groupMatchesByBracketCell(eliminationMatches);
    const showEliminationHeading =
      (currentClass?.type === ClassTypeEnum.TEAMS &&
        (currentClass?.format === ClassFormatEnum.GROUP ||
          currentClass?.format === ClassFormatEnum.SINGLE_BRACKET)) ||
      (!currentClass?.isHasSubClasses &&
        currentClass?.type !== ClassTypeEnum.TEAMS &&
        !isBoxLeagueMode);
    const showEliminationAdd = !currentClass?.isHasSubClasses && !isBoxLeagueMode;

    const isAssignAvailable = useMemo(() => {
      const matchesArray: Array<IMatch> = Object.values(matches);
      if (!matchesArray.length) {
        return false;
      }

      if (currentClass?.format === ClassFormatEnum.BOX_LEAGUE_ELIM) {
        const numRounds = currentClass?.boxLeagueNumOfRounds ?? 1;
        return boxLeagueElimAssignFinalButtonVisible(matchesArray, numRounds);
      }

      const isGroupMatchesCompleted = !((matchesArray as Array<IMatch>) || [])
        .filter(({ tournamentGroup }) => !!tournamentGroup)
        .find(({ results }) => {
          return !results || !results.length;
        });
      const noEliminationPlayersAssign = !((matchesArray as Array<IMatch>) || [])
        .filter(({ tournamentGroup }) => !tournamentGroup)
        .find(({ awayTeam, homeTeam }) => {
          return awayTeam || homeTeam;
        });

      return isGroupMatchesCompleted && noEliminationPlayersAssign;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matches, currentClass]);

    const isScoreAvailable = useMemo(() => {
      const matchesArray: Array<IMatch> = Object.values(matches);
      const isGroupMatchesStarted = ((matchesArray as Array<IMatch>) || [])
        .filter(({ tournamentGroup }) => !!tournamentGroup)
        .every(({ status }) => {
          return status === 0;
        });

      const noEliminationPlayersAssign = ((matchesArray as Array<IMatch>) || [])
        .filter(({ tournamentGroup }) => !tournamentGroup)
        .every(({ awayTeam, homeTeam }) => {
          return !awayTeam && !homeTeam;
        });

      return !!matchesArray.length && isGroupMatchesStarted && noEliminationPlayersAssign;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matches, currentClass]);

    return (
      <>
        <FieldArray name="tournamentMatches">
          {() =>
            courts?.length ? (
              <>
                {((currentClass?.format === ClassFormatEnum.GROUP ||
                  currentClass?.format === ClassFormatEnum.GROUP_BRACKET ||
                  currentClass?.format === ClassFormatEnum.GROUP_ONLY ||
                  isBoxLeagueMode) &&
                  !currentClass?.parentClassId &&
                  groups.length && (
                    <>
                      {(currentClass?.format === ClassFormatEnum.GROUP ||
                        currentClass?.format === ClassFormatEnum.GROUP_BRACKET ||
                        currentClass?.format === ClassFormatEnum.GROUP_ONLY ||
                        isBoxLeagueMode) &&
                      currentClassId ? (
                        <StandingsExplainPanel classId={currentClassId as number} />
                      ) : null}
                      {groups.map((group) => {
                        if (typeof group.id !== 'number') {
                          return null;
                        }

                        const groupMatches = Object.values(matches)
                          .sort((a, b) => Number(a.sortKey) - Number(b.sortKey))
                          .filter((match) => match?.tournamentGroup?.id === group.id);

                        if (isTeamsGroupBracketCells) {
                          const bracketCellGroups = groupMatchesByBracketCell(groupMatches);
                          return (
                            <React.Fragment key={group.id}>
                              <div className="my-3">
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                                  <h5 className="font-weight-bold mb-0">{group.name}</h5>
                                  <div className="d-flex flex-wrap align-items-center gap-2">
                                    <MatchupSectionToolbar
                                      cellGroups={bracketCellGroups}
                                      formikArg={formikArg}
                                      variant="group"
                                      sectionId={`group-${group.id}`}
                                      fallbackRubberCount={groupBracketCellRubberCount}
                                    />
                                    <CustomButton
                                      variant="outline-dark"
                                      btnText={t(buttonPrefix + 'addBracketCell')}
                                      disabled={groupBracketCellRubberCount === 0}
                                      cb={() => {
                                        handleAddBracketCell({
                                          variant: 'group',
                                          tournamentGroupId: group.id as number,
                                          progressionMode: progressionModeForSection(
                                            bracketCellGroups,
                                            'group'
                                          ),
                                        });
                                      }}
                                      className="d-flex justify-content-between align-items-center"
                                    >
                                      <AddIcon />
                                    </CustomButton>
                                    {!!groupMatches.length && (
                                      <ConfirmModal
                                        onConfirm={() => {
                                          recordGroupMatchesRemoval(formikArg, group.id);
                                        }}
                                      >
                                        <CustomButton
                                          className="d-flex align-items-center"
                                          btnText={t(buttonPrefix + 'removeAllMatches')}
                                          variant="outline-dark"
                                        >
                                          <CloseCircle />
                                        </CustomButton>
                                      </ConfirmModal>
                                    )}
                                  </div>
                                </div>
                                <GroupStandings
                                  classId={currentClassId as number}
                                  groupId={group.id}
                                  categoryStandings={teamCategoryStandings}
                                  isTeamsClass={true}
                                  rankingUsesTeamPoints={rankingUsesTeamPoints}
                                />
                              </div>
                              {bracketCellGroups.map((cellGroup) =>
                                !cellGroup.isLegacySingle ? (
                                  <BracketCellSeries
                                    key={cellGroup.cellId}
                                    group={cellGroup}
                                    variant="group"
                                    groupName={group.name}
                                    matchesMap={matches}
                                    currentClass={currentClass}
                                    courts={courts}
                                    formikArg={formikArg}
                                    handleAddTeam={handleAddTeam}
                                    handleAddTeamForBracketCell={handleAddTeamForBracketCell}
                                    handleAddPlayer={handleAddPlayer}
                                    handleOpenResultModal={handleOpenResultModal}
                                    onPlayDataRefresh={onPlayDataRefresh}
                                  />
                                ) : (
                                  <React.Fragment key={cellGroup.cellId}>
                                    {cellGroup.matches.map((match, index) => (
                                      <div key={match.id} className="container-fluid px-0 my-2">
                                        <Match
                                          index={index}
                                          matches={matches}
                                          match={match}
                                          currentClass={currentClass}
                                          courts={courts}
                                          formikArg={formikArg}
                                          handleAddTeam={handleAddTeam}
                                          handleAddPlayer={handleAddPlayer}
                                          handleOpenResultModal={handleOpenResultModal}
                                          isGroup={true}
                                          sortVs={true}
                                        />
                                      </div>
                                    ))}
                                  </React.Fragment>
                                )
                              )}
                            </React.Fragment>
                          );
                        }

                        let sortVs = false;
                        const combinations: {
                          id: number;
                          homeTeamId: number;
                          awayTeamId: number;
                          name: string;
                        }[] = [];
                        if (currentClass?.type === ClassTypeEnum.TEAMS) {
                          sortVs = true;
                          const teams = group.teams;

                          let categoryId = 0;
                          for (let i = 0; i < teams.length; i++) {
                            for (let t = i + 1; t < teams.length; t++) {
                              const home = teams[i];
                              const away = teams[t];
                              combinations.push({
                                id: categoryId,
                                homeTeamId: home?.id,
                                awayTeamId: away?.id,
                                name: `${home?.name || 'Missing Team'} vs ${
                                  away?.name || 'Missing Team'
                                }`,
                              });
                              categoryId += 1;
                            }
                          }
                        }

                        const flatMoveBoxKey = `flat-box-${group.id}`;

                        let matchesBySubsection: {
                          [subSectionId: string]: {
                            name: string;
                            matches: IMatch[];
                            round?: number | null;
                          };
                        };

                        if (isBoxLeagueMode) {
                          matchesBySubsection = {
                            [flatMoveBoxKey]: {
                              name: t(prefix + 'generalMatches'),
                              matches: groupMatches,
                              round: 0,
                            },
                          };
                        } else if (currentClass?.type === ClassTypeEnum.TEAMS) {
                          matchesBySubsection = groupMatches.reduce(
                            (
                              acc: {
                                [subSectionId: string]: {
                                  name: string;
                                  matches: IMatch[];
                                  round?: number | null;
                                };
                              },
                              match: IMatch
                            ) => {
                              if (sortVs) {
                                combinations.forEach((combination) => {
                                  const categoryId = combination.id;
                                  const homeTeamId = combination.homeTeamId;
                                  const awayTeamId = combination.awayTeamId;
                                  let categoryName = null;
                                  if (
                                    match.homeTeam?.id === homeTeamId &&
                                    match.awayTeam?.id === awayTeamId
                                  ) {
                                    categoryName = combination.name;
                                  } else if (
                                    match.homeTeam?.id === awayTeamId &&
                                    match.awayTeam?.id === homeTeamId
                                  ) {
                                    categoryName = combination.name;
                                  }
                                  if (categoryName) {
                                    if (!acc[categoryId]) {
                                      acc[categoryId] = {
                                        name: categoryName,
                                        matches: [],
                                        round: match.type,
                                      };
                                    }

                                    acc[categoryId].matches.push(match);
                                  }
                                });
                              } else {
                                const categoryId = match.tournamentCategory?.id || 'uncategorized';
                                if (!acc[categoryId]) {
                                  const category = currentClass?.tournamentCategories?.find(
                                    (cat: any) => cat.id === categoryId
                                  );

                                  const categoryName =
                                    categoryId === 'uncategorized'
                                      ? t(prefix + 'generalMatches')
                                      : category?.name || 'Unknown Category';

                                  acc[categoryId] = {
                                    name: categoryName,
                                    matches: [],
                                    round: match.type,
                                  };
                                }
                                acc[categoryId].matches.push(match);
                              }

                              return acc;
                            },
                            {}
                          );
                        } else {
                          matchesBySubsection = groupMatches.reduce(
                            (
                              acc: {
                                [subSectionId: string]: {
                                  name: string;
                                  matches: IMatch[];
                                  round: number | null;
                                };
                              },
                              match: IMatch
                            ) => {
                              const roundNum = t(prefix + 'generalMatches');
                              if (!acc[roundNum]) {
                                acc[roundNum] = {
                                  name: `${roundNum}`,
                                  matches: [],
                                  round: null,
                                };
                              }
                              acc[roundNum].matches.push(match);
                              return acc;
                            },
                            {}
                          );
                        }

                        return (
                          <React.Fragment key={group.id}>
                            <div className="my-3">
                              <div>
                                <h5 className="font-weight-bold mb-0">{group.name}</h5>
                              </div>
                              {!!groupMatches.length && (
                                <ConfirmModal
                                  onConfirm={() => {
                                    recordGroupMatchesRemoval(formikArg, group.id);
                                  }}
                                >
                                  <CustomButton
                                    className="d-flex align-items-center"
                                    btnText={t(buttonPrefix + 'removeAllMatches')}
                                    variant="outline-dark"
                                  >
                                    <CloseCircle />
                                  </CustomButton>
                                </ConfirmModal>
                              )}
                              {isBoxLeagueMode && (
                                <GroupStandings
                                  classId={currentClassId as number}
                                  groupId={group.id}
                                  roundNumber={
                                    group.boxLeagueRoundNumber != null
                                      ? Number(group.boxLeagueRoundNumber)
                                      : 1
                                  }
                                  categoryStandings={teamCategoryStandings}
                                  isTeamsClass={currentClass?.type === ClassTypeEnum.TEAMS}
                                  rankingUsesTeamPoints={rankingUsesTeamPoints}
                                />
                              )}
                              {!isBoxLeagueMode && (
                                <GroupStandings
                                  classId={currentClassId as number}
                                  groupId={group.id}
                                  categoryStandings={teamCategoryStandings}
                                  isTeamsClass={currentClass?.type === ClassTypeEnum.TEAMS}
                                  rankingUsesTeamPoints={rankingUsesTeamPoints}
                                />
                              )}
                            </div>
                            {Object.keys(matchesBySubsection)
                              .sort((a, b) => {
                                if (currentClass?.type === ClassTypeEnum.TEAMS || isBoxLeagueMode) {
                                  const roundA = matchesBySubsection[a].round || 0;
                                  const roundB = matchesBySubsection[b].round || 0;

                                  if (roundA !== roundB) return roundA - roundB;

                                  const numA = isNaN(Number(a)) ? 999999 : Number(a);
                                  const numB = isNaN(Number(b)) ? 999999 : Number(b);
                                  return numA - numB;
                                }

                                return a.localeCompare(b);
                              })
                              .map((subsectionId) => {
                                const subsection = matchesBySubsection[subsectionId];
                                const round = subsection.round || 0;
                                const isFlatBoxLeagueSubsection =
                                  isBoxLeagueMode && subsectionId === flatMoveBoxKey;

                                const formikCategory = formikArg.values.tournamentCategories?.find(
                                  (c: any) => c.id.toString() === subsectionId.toString()
                                );

                                const displayTitle = formikCategory
                                  ? formikCategory.name
                                  : subsection.name;

                                // Add match handler for this subsection
                                const handleAddMatchForSubsection = () => {
                                  const id = `99999999-${uniqId()}`;
                                  const newMatchBase = {
                                    ...matchData,
                                    name: t('match-defaultMatchName'),
                                    id,
                                    startDate: '',
                                    homeTeamPlaceholder: '',
                                    awayTeamPlaceholder: '',
                                    court: null,
                                    tournamentGroup: {
                                      id: group.id,
                                    },
                                  };

                                  // Add the correct property based on the subsection context
                                  if (sortVs) {
                                    const combo = combinations[Number(subsectionId)];
                                    if (combo) {
                                      const homeTeam = group.teams.find(
                                        (t) => t.id === combo.homeTeamId
                                      );
                                      const awayTeam = group.teams.find(
                                        (t) => t.id === combo.awayTeamId
                                      );

                                      newMatchBase.homeTeam = homeTeam || null;
                                      newMatchBase.awayTeam = awayTeam || null;
                                    }
                                    formikArg.setFieldValue('tournamentMatches', {
                                      ...matches,
                                      [id]: newMatchBase,
                                    });
                                  } else if (
                                    (currentClass?.type === ClassTypeEnum.TEAMS ||
                                      isBoxLeagueMode) &&
                                    (subsectionId !== 'uncategorized' || isFlatBoxLeagueSubsection)
                                  ) {
                                    let categoryIdForNew: string | number = subsectionId;
                                    if (isFlatBoxLeagueSubsection) {
                                      const ladderRound =
                                        group.boxLeagueRoundNumber != null
                                          ? Number(group.boxLeagueRoundNumber)
                                          : 1;
                                      const cat = currentClass?.tournamentCategories?.find(
                                        (c: any) => Number(c.roundNumber) === ladderRound
                                      );
                                      if (cat?.id != null) {
                                        categoryIdForNew = cat.id;
                                      } else {
                                        const firstCatId = groupMatches.find(
                                          (m) => m.tournamentCategory?.id != null
                                        )?.tournamentCategory?.id;
                                        if (firstCatId != null) {
                                          categoryIdForNew = firstCatId;
                                        }
                                      }
                                    }
                                    const newMatchBaseCategory = {
                                      ...newMatchBase,
                                      tournamentCategory: {
                                        id: categoryIdForNew,
                                      },
                                    };

                                    formikArg.setFieldValue('tournamentMatches', {
                                      ...matches,
                                      [id]: newMatchBaseCategory,
                                    });
                                  } else {
                                    formikArg.setFieldValue('tournamentMatches', {
                                      ...matches,
                                      [id]: newMatchBase,
                                    });
                                  }
                                };

                                return (
                                  <MatchGroupSubsection
                                    key={subsectionId}
                                    title={displayTitle}
                                    categoryId={subsectionId}
                                    matchesToDisplay={subsection.matches}
                                    isCollapsible={subsectionId !== 'uncategorized'}
                                    defaultOpen={subsection.matches.length > 0}
                                    isEditable={
                                      subsectionId !== 'uncategorized' &&
                                      !(isBoxLeagueMode && isFlatBoxLeagueSubsection)
                                    }
                                    onTitleChange={handleCategoryRename}
                                    onAddMatch={handleAddMatchForSubsection}
                                    allMatches={matches}
                                    currentClass={currentClass}
                                    courts={courts}
                                    formikArg={formikArg}
                                    handleAddTeam={handleAddTeam}
                                    handleAddPlayer={handleAddPlayer}
                                    handleOpenResultModal={handleOpenResultModal}
                                    sortVs={sortVs}
                                    roundNumber={isFlatBoxLeagueSubsection ? 0 : round}
                                    groupId={+group.id}
                                    categoryStandings={teamCategoryStandings}
                                    isTeamsClass={currentClass?.type === ClassTypeEnum.TEAMS}
                                    rankingUsesTeamPoints={rankingUsesTeamPoints}
                                  />
                                );
                              })}
                          </React.Fragment>
                        );
                      })}
                    </>
                  )) ||
                  null}
                <div className="my-3">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    {showEliminationHeading ? (
                      <h5 className="font-weight-bold mb-0">{t(prefix + 'elimination')}</h5>
                    ) : null}
                    <div className="d-flex flex-wrap align-items-center gap-2 ms-auto">
                      {showEliminationBracketCells && isTeamsClass ? (
                        <MatchupSectionToolbar
                          cellGroups={eliminationBracketCellGroups}
                          formikArg={formikArg}
                          variant="elimination"
                          sectionId="elimination"
                          fallbackRubberCount={eliminationBracketCellRubberCount}
                        />
                      ) : null}
                      {showEliminationAdd && isTeamsClass ? (
                        <CustomButton
                          variant="outline-dark"
                          btnText={t(buttonPrefix + 'addBracketCell')}
                          disabled={eliminationBracketCellRubberCount === 0}
                          cb={() => {
                            handleAddBracketCell({
                              variant: 'elimination',
                              progressionMode: progressionModeForSection(
                                eliminationBracketCellGroups,
                                'elimination'
                              ),
                            });
                          }}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <AddIcon />
                        </CustomButton>
                      ) : null}
                      {showEliminationAdd && !isTeamsClass ? (
                        <CustomButton
                          variant="outline-dark"
                          btnText={t(buttonPrefix + 'addMatch')}
                          cb={() => {
                            const id = `99999999-${uniqId()}`;
                            formikArg.setFieldValue(
                              'tournamentMatches',
                              Object.assign(
                                {
                                  [id]: {
                                    ...matchData,
                                    name: t('match-defaultMatchName'),
                                    id,
                                    homeTeamPlaceholder: '',
                                    awayTeamPlaceholder: '',
                                    startDate: '',
                                    court: null,
                                    tournamentGroup: null,
                                  },
                                },
                                matches
                              )
                            );
                          }}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <AddIcon />
                        </CustomButton>
                      ) : null}
                      {!!eliminationMatches.length && (
                        <ConfirmModal
                          onConfirm={() => {
                            recordEliminationMatchesRemoval(formikArg);
                          }}
                        >
                          <CustomButton
                            className="d-flex align-items-center"
                            btnText={t(buttonPrefix + 'removeAllMatches')}
                            variant="outline-dark"
                          >
                            <CloseCircle />
                          </CustomButton>
                        </ConfirmModal>
                      )}
                    </div>
                  </div>
                  {isAssignAvailable && currentClass?.format === ClassFormatEnum.BOX_LEAGUE_ELIM && (
                    <div className="my-3 d-flex align-items-center justify-content-between">
                      <CustomButton
                        variant="outline-dark"
                        btnText={t(buttonPrefix + 'assignTeams')}
                        className="d-flex justify-content-between align-items-center ms-3"
                        cb={() => {
                          dispatch(assignTeamsForElmMatchesForGroup(currentClassId as number));
                        }}
                      />
                    </div>
                  )}
                  {devTools && isScoreAvailable && (
                    <div className="my-3 d-flex">
                      <CustomButton
                        variant="outline-dark"
                        btnText="Random Score Group Matches (DEV ONLY)"
                        className="d-flex justify-content-between align-items-center ms-3"
                        cb={() => {
                          dispatch(devAutoScoreGroupMatches(currentClassId as number));
                        }}
                      />
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            DEV ONLY - Will automatically score all group matches, random scores up
                            to 11.
                          </Tooltip>
                        }
                      >
                        <InfoCircleFill className="ml-2" />
                      </OverlayTrigger>
                    </div>
                  )}
                </div>
                {eliminationMatches.length && !currentClass?.isHasSubClasses
                  ? eliminationBracketCellGroups.map((group) =>
                      !group.isLegacySingle && showEliminationBracketCells ? (
                        <BracketCellSeries
                          key={group.cellId}
                          group={group}
                          matchesMap={matches}
                          currentClass={currentClass}
                          courts={courts}
                          formikArg={formikArg}
                          handleAddTeam={handleAddTeam}
                          handleAddTeamForBracketCell={handleAddTeamForBracketCell}
                          handleAddPlayer={handleAddPlayer}
                          handleOpenResultModal={handleOpenResultModal}
                          onPlayDataRefresh={onPlayDataRefresh}
                        />
                      ) : (
                        <React.Fragment key={group.cellId}>
                          {group.matches.map((match, index) => (
                            <div key={match.id} className="container-fluid px-0 my-2">
                              <Match
                                index={index}
                                matches={matches}
                                match={match}
                                currentClass={currentClass}
                                courts={courts}
                                formikArg={formikArg}
                                handleAddTeam={handleAddTeam}
                                handleAddPlayer={handleAddPlayer}
                                handleOpenResultModal={handleOpenResultModal}
                              />
                            </div>
                          ))}
                        </React.Fragment>
                      )
                    )
                  : null}
              </>
            ) : (
              <div className="d-flex justify-content-end fw-lighter">
                <p>{t(prefix + 'warning6')}</p>
              </div>
            )
          }
        </FieldArray>
      </>
    );
  }
);
